import { createColumnHelper } from '@tanstack/react-table';
import { PlusIcon } from 'lucide-react';
import { useMemo } from 'react';
import * as z from 'zod';

import { DimensionUnit, WeightUnit } from '@invenco/common-interface/shared';
import type { QuotePackageDTO } from '@invenco/shipping-interface';

import {
  CollapsibleCard,
  CollapsibleCardContent,
  CollapsibleCardTrigger,
} from '@/components/collapsible-card';
import { Button } from '@/components/core/button';
import { CardAction, CardHeader } from '@/components/core/card';
import { EditableDataTable, useEditableRowDataTable } from '@/components/data-table';
import { DataTableCardBody, DataTableCardTitle } from '@/components/data-table/data-table-card';
import { formatNumber } from '@/shared/helpers';

export type QuotePackageRow = {
  id: string;
  name: string;
  weight: number;
  width: number;
  height: number;
  length: number;
};

type QuotePackagesCardProps = {
  packages: QuotePackageRow[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (packages: QuotePackageRow[]) => void;
};

const columnHelper = createColumnHelper<QuotePackageRow>();

const packageSchema = z.object({
  weight: z.coerce.number().positive(),
  width: z.coerce.number().positive(),
  height: z.coerce.number().positive(),
  length: z.coerce.number().positive(),
});

const packageColumns = [
  columnHelper.accessor('name', {
    header: 'Name',
  }),
  columnHelper.accessor('weight', {
    header: 'Weight (KG)',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: {
      align: 'right',
      editable: {
        label: 'Weight (KG)',
        editor: { type: 'number', min: 0, step: 'any' },
      },
    },
  }),
  columnHelper.accessor('width', {
    header: 'Width (CM)',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: {
      align: 'right',
      editable: {
        label: 'Width (CM)',
        editor: { type: 'number', min: 0, step: 'any' },
      },
    },
  }),
  columnHelper.accessor('height', {
    header: 'Height (CM)',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: {
      align: 'right',
      editable: {
        label: 'Height (CM)',
        editor: { type: 'number', min: 0, step: 'any' },
      },
    },
  }),
  columnHelper.accessor('length', {
    header: 'Length (CM)',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: {
      align: 'right',
      editable: {
        label: 'Length (CM)',
        editor: { type: 'number', min: 0, step: 'any' },
      },
    },
  }),
];

function getPackageName(index: number) {
  return `Package ${index + 1}`;
}

function getPackageId(index: number) {
  return `package-${index + 1}`;
}

function createPackageRow(index: number): QuotePackageRow {
  return {
    id: getPackageId(index),
    name: getPackageName(index),
    weight: 1,
    width: 10,
    height: 10,
    length: 10,
  };
}

function reindexPackages(packages: QuotePackageRow[]) {
  return packages.map((pkg, index) => ({
    ...pkg,
    id: getPackageId(index),
    name: getPackageName(index),
  }));
}

export function getInitialQuotePackages(): QuotePackageRow[] {
  return [createPackageRow(0)];
}

export function toQuotePackageDTO(pkg: QuotePackageRow): QuotePackageDTO {
  return {
    name: pkg.name,
    weight: { value: pkg.weight, unit: WeightUnit.KG },
    dimensions: {
      width: pkg.width,
      height: pkg.height,
      length: pkg.length,
      unit: DimensionUnit.CM,
    },
  };
}

export function QuotePackagesCard({
  packages,
  open,
  onOpenChange,
  onChange,
}: QuotePackagesCardProps) {
  const columns = useMemo(() => packageColumns, []);
  const {
    table,
    editingRowId,
    startEditingRow,
    onClickRow,
    onCancelEdit,
    ...editableDataTableProps
  } = useEditableRowDataTable({
    data: packages,
    columns,
    schema: packageSchema,
    // This card has no pagination controls, so opt out of client pagination — otherwise
    // TanStack's default 10-row page size silently hides rows (including the draft row).
    manualPagination: true,
    manualSorting: false,
    onSaveRow: async (pkg, values) => {
      onChange(packages.map((row) => (row.id === pkg.id ? { ...row, ...values } : row)));
    },
    onDeleteRow: async (pkg) => {
      onChange(reindexPackages(packages.filter((row) => row.id !== pkg.id)));
    },
  });

  function handleAddPackage() {
    const nextPackage = createPackageRow(packages.length);
    onChange([...packages, nextPackage]);
    startEditingRow(nextPackage.id, nextPackage, { columnId: 'weight' });
  }

  return (
    <CollapsibleCard aria-label="Packages" open={open} onOpenChange={onOpenChange}>
      <CardHeader>
        <DataTableCardTitle table={table} rowCount={packages.length}>
          Packages
        </DataTableCardTitle>
        <CardAction>
          <CollapsibleCardTrigger aria-label="Toggle packages" />
        </CardAction>
      </CardHeader>
      <CollapsibleCardContent>
        <DataTableCardBody table={table} emptyMessage="Add a package to create a quote">
          <EditableDataTable
            table={table}
            editingRowId={editingRowId}
            {...editableDataTableProps}
            onClickRow={onClickRow}
            onCancelEdit={onCancelEdit}
          />
        </DataTableCardBody>
        <div className="flex h-10 items-stretch border-t [[data-slot=card-header]+&]:border-t-0">
          <Button
            type="button"
            variant="outline"
            className="h-full w-full justify-start rounded-none border-0 px-4 shadow-none"
            onClick={handleAddPackage}
          >
            <PlusIcon />
            Add package
          </Button>
        </div>
      </CollapsibleCardContent>
    </CollapsibleCard>
  );
}
